import React, { useState } from 'react';

// A real connected pipeline rendered with the SAME node classes used on the
// canvas, so the preview matches the actual product exactly.
function PreviewPipeline() {
  return (
    <div className="auth-flow">
      {/* Collection */}
      <div className="pipeline-node collection-node auth-node">
        <div className="node-header collection-header">
          <span className="node-type-icon">⊞</span>
          <span className="node-type-label">Collection</span>
          <span className="node-agent-count" style={{ background: '#0d9488' }}>3</span>
        </div>
        <div className="node-body">
          <div className="node-title">Tickets</div>
          <div className="node-description">Support tickets</div>
          <div className="collection-meta">
            <span className="col-meta-pill">3 fields</span>
            <span className="col-meta-pill">3 records</span>
          </div>
          <div className="collection-status-row">
            <span className="col-status-chip" style={{ background: '#3b82f622', color: '#3b82f6' }}>Open 2</span>
            <span className="col-status-chip" style={{ background: '#10b98122', color: '#10b981' }}>Done 1</span>
          </div>
        </div>
      </div>

      <Edge />

      {/* Trigger */}
      <div className="pipeline-node trigger-node auth-node">
        <div className="node-header trigger-header">
          <span className="node-type-icon">⚡</span>
          <span className="node-type-label">Trigger</span>
        </div>
        <div className="node-body">
          <div className="node-title">New Ticket Added</div>
          <div className="node-description">Fires when a record is added to Tickets</div>
          <div className="trigger-type-chip">
            <span className="trigger-chip-icon">➕</span>
            <span>Record added</span>
          </div>
        </div>
      </div>

      <Edge />

      {/* Action */}
      <div className="pipeline-node action-node auth-node">
        <div className="node-header action-header">
          <span className="node-type-icon">⚙</span>
          <span className="node-type-label">Action</span>
        </div>
        <div className="node-body">
          <div className="node-title">Set Status to Open</div>
          <div className="node-description">Auto-set new tickets to Open</div>
          <div className="action-type-chip">
            <span className="action-chip-icon">✏️</span>
            <span>Update field</span>
          </div>
          <div className="action-summary">Set Status → "Open"</div>
        </div>
      </div>
    </div>
  );
}

// SVG marching-ants edge — animated exactly like the real canvas connectors.
const Edge = () => (
  <svg className="auth-edge" width="46" height="12" viewBox="0 0 46 12" aria-hidden="true">
    <line x1="0" y1="6" x2="36" y2="6" className="auth-edge-line" />
    <polygon points="36,1.5 46,6 36,10.5" className="auth-edge-arrow" />
  </svg>
);

function AuthScreen({ onAuth, onGoogleAuth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [username, setUsername] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const switchMode = (login) => { setIsLogin(login); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isLogin) {
      if (!username.trim()) { setError('Please choose a username'); return; }
      if (password !== confirm) { setError('Passwords don\'t match'); return; }
      if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    }

    setLoading(true);
    try {
      await onAuth(email, password, isLogin, username);
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError('');
    try {
      await onGoogleAuth();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-left">
        <div className="auth-hero">
          <img src="/logo.png" alt="Projectly" className="auth-logo" />
          <h1 className="auth-wordmark">Projectly</h1>
          <p className="auth-tagline">
            Automate your data, approvals &amp; tools — <span className="auth-grad">visually</span>.
          </p>
        </div>

        <PreviewPipeline />
        <p className="auth-preview-cap">An example pipeline — new tickets get triaged and confirmed, automatically.</p>
      </div>

      <div className="auth-card">
        <div className="auth-tabs" role="tablist">
          <button data-testid="auth-tab-signin" className={`auth-tab${isLogin ? ' active' : ''}`} onClick={() => switchMode(true)} type="button">Sign in</button>
          <button data-testid="auth-tab-signup" className={`auth-tab${!isLogin ? ' active' : ''}`} onClick={() => switchMode(false)} type="button">Sign up</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="button" className="google-auth-btn" onClick={handleGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: '10px', flexShrink: 0 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        <div className="auth-divider"><span>or {isLogin ? 'sign in' : 'sign up'} with email</span></div>

        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-field">
            <span>Email</span>
            <input data-testid="auth-email" type="email" placeholder="you@company.com" value={email}
              onChange={(e) => setEmail(e.target.value)} required disabled={loading} autoComplete="email" />
          </label>

          {!isLogin && (
            <label className="auth-field">
              <span>Username</span>
              <input data-testid="auth-username" type="text" placeholder="How should we address you?" value={username}
                onChange={(e) => setUsername(e.target.value)} required disabled={loading} autoComplete="username" />
            </label>
          )}

          <label className="auth-field">
            <span>Password</span>
            <input data-testid="auth-password" type="password" placeholder={isLogin ? 'Your password' : 'At least 6 characters'} value={password}
              onChange={(e) => setPassword(e.target.value)} required disabled={loading}
              autoComplete={isLogin ? 'current-password' : 'new-password'} />
          </label>

          {!isLogin && (
            <label className="auth-field">
              <span>Confirm password</span>
              <input data-testid="auth-confirm" type="password" placeholder="Re-enter your password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required disabled={loading} autoComplete="new-password" />
            </label>
          )}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Please wait…' : (isLogin ? 'Sign in' : 'Create account')}
          </button>
        </form>

        <p className="auth-switch">
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <button type="button" className="auth-switch-link" onClick={() => switchMode(!isLogin)}>
            {isLogin ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );
}

export default AuthScreen;
